import { FC, useCallback, useState, useEffect } from 'react';
import classnames from 'classnames';
import { trackEvent } from 'dibs-tracking';
import { graphql, useFragment } from 'react-relay';
import SV from 'server-vars';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient';
import {
    getIsOptinPromptShown,
    getSessionPdpItemSaved,
    setSessionPdpItemSaved,
} from '../../PdpSharedOptInPromptModal/pdpSharedOptInPromptModaHelpers';
import { usePdpSharedTracking } from '../../hooks/usePdpSharedTracking';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { useRespUnavailableContext } from '../PdpRespUnavailableContext/PdpRespUnavailableContext';
import { PdpRespSharedItemsRefetchCarouselConfig } from '../../respShared/PdpRespSharedItemsRefetchCarousel/PdpRespSharedItemsRefetchCarouselConfig';
import {
    AUCTION_SIDERAIL_REDESIGN,
    ENHANCED_BUYER_INCENTIVES,
} from '../../../constants/abTestsConstants';
import { trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2';
import { useEnhancedBuyerIncentivesClientVariant } from '../../hooks/useEnhancedBuyerIncentivesClientVariant';

// components
import { PriceDisplay } from 'dibs-buyer-price';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import { PdpRespSharedTitle } from '../../respShared/PdpRespSharedTitle/PdpRespSharedTitle';
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy';
import { PdpRespUnavailableHeader } from '../PdpRespUnavailableHeader/PdpRespUnavailableHeader';
import { SharedRespLinksBlock } from '../../../shared/SharedRespLinksBlock/SharedRespLinksBlock';
import { RecentlyViewedItemsProvider } from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';
import { PdpRespUnavailableHeaderYMAL } from '../PdpRespUnavailableHeaderYMAL/PdpRespUnavailableHeaderYMAL';
import { PdpSharedAuctionEndedLink } from '../../shared/PdpSharedAuctionEndedLink/PdpSharedAuctionEndedLink';
import { PdpRespSharedBreadcrumbs } from '../../respShared/PdpRespSharedBreadcrumbs/PdpRespSharedBreadcrumbs';
import { PdpRespUnavailableDetailsTabs } from '../PdpRespUnavailableDetailsTabs/PdpRespUnavailableDetailsTabs';
import { PdpSharedEditorialArticles } from '../../shared/PdpSharedEditorialArticles/PdpSharedEditorialArticles';
import { PdpRespUnavailableHeroCarousel } from '../PdpRespUnavailableHeroCarousel/PdpRespUnavailableHeroCarousel';
import { PdpRespSharedBuyerAssurance } from '../../respShared/PdpRespSharedBuyerAssurance/PdpRespSharedBuyerAssurance';
import { PdpRespSharedSellerChatLoader } from '../../respShared/PdpRespSharedSellerChatLoader/PdpRespSharedSellerChatLoader';
import { PdpRespSharedItemsRefetchCarousel } from '../../respShared/PdpRespSharedItemsRefetchCarousel/PdpRespSharedItemsRefetchCarousel';
import { PdpRespSimilarAuctionItemsLazy } from '../../PdpRespSimilarAuctionItems/PdpRespSimilarAuctionItemsLazy';
import { PdpRespSharedPrintDetailsLazy } from '../../respShared/PdpRespSharedPrintDetails/PdpRespSharedPrintDetailsLazy';
import { PdpSharedAuctionClosed } from '../../PdpSharedAuctionClosed/PdpSharedAuctionClosed';
import { BrowserHistoryContextProvider } from '../../../global/BrowserHistoryContext/BrowserHistoryContext';
import { useSoldItemLinksPdpClientVariant } from '../../hooks/useSoldItemLinksPdpClientVariant';

// styles
import styles from './PdpRespUnavailableContainer.scss';

// types
import { PdpRespUnavailableContainer_item$key as Item } from './__generated__/PdpRespUnavailableContainer_item.graphql';
import { PdpRespUnavailableContainer_user$key as User } from './__generated__/PdpRespUnavailableContainer_user.graphql';
import { PdpRespUnavailableContainer_viewer$key as Viewer } from './__generated__/PdpRespUnavailableContainer_viewer.graphql';

// lazy components
const PdpRespUnavailableSidebarContact = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../PdpRespUnavailableSidebarContact/PdpRespUnavailableSidebarContact'
        )
);

const PdpRespUnavailableSidebarSellerInfo = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../PdpRespUnavailableSidebarSellerInfo/PdpRespUnavailableSidebarSellerInfo'
        )
);

const PdpSharedItemFavorites = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../shared/PdpSharedItemFavorites/PdpSharedItemFavorites'
        )
);

const PdpRespSharedHeroCarouselPrint = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../respShared/PdpRespSharedHeroCarouselPrint/PdpRespSharedHeroCarouselPrint'
        )
);

const PdpRespSharedQrCode = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../respShared/PdpRespSharedQrCode/PdpRespSharedQrCode'
        )
);

const PdpSharedOptInPromptModal = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../PdpSharedOptInPromptModal/PdpSharedOptInPromptModal'
        )
);

const viewerFragment = graphql`
    fragment PdpRespUnavailableContainer_viewer on Viewer {
        pdpOptinPromptFF: featureFlag(feature: "pdpOptinPrompt")
        isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
        ...usePdpSharedTracking_viewer
        ...SharedRespLinksBlock_viewer
        ...PdpRespUnavailableHeaderYMAL_viewer
        ...PdpRespUnavailableDetailsTabs_viewer
        ...PdpRespUnavailableHeroCarousel_viewer
        ...PdpRespSharedItemsRefetchCarouselConfig_YMAL_viewer
        ...PdpRespSharedItemsRefetchCarouselConfig_sellerYMAL_viewer
        ...PdpRespSharedSellerChatLoader_viewer @include(if: $fetchChat)
        ...PdpRespSimilarAuctionItemsLazy_viewer
    }
`;
const itemFragment = graphql`
    fragment PdpRespUnavailableContainer_item on Item {
        serviceId
        isAuctionEnabled
        isSold
        seller {
            serviceId
            isAccessible
        }
        internalBuyLinks {
            ...SharedRespLinksBlock_links
        }
        ...PdpRespSharedTitle_item
        ...usePdpSharedTracking_item
        ...SharedRespLinksBlock_item
        ...PdpRespUnavailableHeader_item
        ...PdpRespSharedBreadcrumbs_item
        ...PdpRespUnavailableDetailsTabs_item
        ...PdpRespUnavailableHeroCarousel_item
        ...PdpSharedEditorialArticles_refactor_item
        ...PdpRespSharedPrintDetailsLazy_item
        ...PdpSharedAuctionClosed_item @include(if: $afterDisplay)
        ...PriceDisplay_item
            @include(if: $afterDisplay)
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...PdpRespSharedQrCode_item @include(if: $afterDisplay)
        ...PdpSharedItemFavorites_item @include(if: $afterDisplay)
        ...PdpRespSharedHeroCarouselPrint_item @include(if: $afterDisplay)
        ...PdpRespUnavailableSidebarContact_item @include(if: $afterDisplay)
        ...PdpRespUnavailableSidebarSellerInfo_item @include(if: $afterDisplay)
        ...useSoldItemLinksPdpClientVariant_item
    }
`;
const userFragment = graphql`
    fragment PdpRespUnavailableContainer_user on User {
        serviceId
        ...PdpRespUnavailableHeader_user
        ...PdpRespSharedBuyerAssurance_user
        ...PdpRespUnavailableDetailsTabs_user
        ...PdpRespUnavailableHeroCarousel_user
        ...PdpRespSharedItemsRefetchCarousel_user
        ...PdpRespSharedPrintDetailsLazy_user
        ...PdpSharedItemFavorites_user @include(if: $afterDisplay)
        ...PdpRespUnavailableSidebarContact_user @include(if: $afterDisplay)
        ...PdpRespUnavailableSidebarSellerInfo_user @include(if: $afterDisplay)
        ...PdpRespSimilarAuctionItemsLazy_user
    }
`;

const isAuctionModuleDlosPDPVariant = !!SV.get('isAuctionModuleDlosPDPVariant');

export const PdpRespUnavailableContainer: FC<{
    item: Item;
    viewer: Viewer;
    user: User | null;
}> = ({ item: itemRef, user: userRef, viewer: viewerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const { isAuctionSiderailRedesignVariant } = useServerVarsContext();
    const isEnhancedBuyerIncentivesClientVariant = useEnhancedBuyerIncentivesClientVariant();
    const {
        currency,
        relayVars: { afterDisplay, fetchChat },
    } = useRespUnavailableContext();
    const [showOptinPrompt, setShowOptinPrompt] = useState(false);

    usePdpSharedTracking({ item, viewer });

    const { serviceId: userId } = user || {};
    const { pdpOptinPromptFF, isWordpressArticlesEnabled } = viewer;
    const { seller, serviceId: itemId, internalBuyLinks, isAuctionEnabled, isSold } = item;

    const isSoldItemLinksPdpClientVariant = useSoldItemLinksPdpClientVariant({ item }) && isSold;

    const onFavorited = useCallback(() => {
        if (!pdpOptinPromptFF) {
            return;
        }
        const isOptinPromptShown = getIsOptinPromptShown();
        const isPdpItemSaved = getSessionPdpItemSaved();
        const shouldShowOptinPrompt = !!userId && !isOptinPromptShown && !isPdpItemSaved;

        if (shouldShowOptinPrompt) {
            setSessionPdpItemSaved();
            setShowOptinPrompt(true);
        }
    }, [userId, pdpOptinPromptFF]);

    useEffect(() => {
        if (isAuctionEnabled) {
            trackAbTestV2Variant(AUCTION_SIDERAIL_REDESIGN);
        }

        if (isSold && !isAuctionEnabled) {
            trackAbTestV2Variant('soldItemLinksPDP');
        }

        trackAbTestV2Variant(ENHANCED_BUYER_INCENTIVES);
    }, [isAuctionEnabled, isSold]);

    const shouldRenderAuctionsSidebarVariant = isAuctionEnabled && isAuctionSiderailRedesignVariant;

    return (
        <>
            <div className={styles.printHide}>
                <div className={styles.topModuleContainer}>
                    <div className={styles.topModuleContainerInner}>
                        <PdpRespUnavailableHeader item={item} user={user} />
                        <div className={styles.divider} />
                        <PdpRespUnavailableHeaderYMAL viewer={viewer} />
                    </div>
                </div>
                <PdpRespSharedBreadcrumbs item={item} />
            </div>
            <div className="rowFlex" data-tn="pdp-main-container">
                <div
                    className={`${styles.topContainer} ${styles.carouselContainer} colXs12 colLg6 colLgOffset1 colXl6 colXlOffset1`}
                >
                    <div className={styles.printHide}>
                        <BrowserHistoryContextProvider>
                            <PdpRespUnavailableHeroCarousel
                                item={item}
                                user={user}
                                viewer={viewer}
                            />
                        </BrowserHistoryContextProvider>
                    </div>
                    <ClientSuspense fallback={null}>
                        <div className={styles.printShow}>
                            <PdpRespSharedHeroCarouselPrint item={item} />
                        </div>
                    </ClientSuspense>
                </div>
                <div className={`${styles.topContainer} colXs12 colLg4 colXl4`}>
                    <div className={styles.sidebarContainer}>
                        {isAuctionEnabled && !shouldRenderAuctionsSidebarVariant && (
                            <PdpSharedAuctionEndedLink />
                        )}
                        <div className="rowFlex">
                            <div className="colXs12 colLg10">
                                <PdpRespSharedTitle item={item} />
                                {!shouldRenderAuctionsSidebarVariant && (
                                    <div className={styles.printHide}>
                                        <ClientSuspense fallback={null}>
                                            <PriceDisplay
                                                sku={null}
                                                useSvgIcon
                                                item={item}
                                                displayVatLabel
                                                currency={currency}
                                                isSoldItemLinksPdpClientVariant={
                                                    isSoldItemLinksPdpClientVariant
                                                }
                                            />
                                        </ClientSuspense>
                                    </div>
                                )}
                            </div>
                            <div
                                className={`${styles.printHide} ${styles.favoritesContainer} colXs2`}
                            >
                                <ClientSuspense fallback={null}>
                                    <PdpSharedItemFavorites
                                        item={item}
                                        user={user}
                                        showHeartCount
                                        onFavorited={onFavorited}
                                        wrapperClass={styles.favoritesWrapper}
                                        heartIconWrapperClass={styles.heartIconWrapper}
                                    />
                                </ClientSuspense>
                            </div>
                        </div>
                        {shouldRenderAuctionsSidebarVariant && (
                            <ClientSuspense fallback={null}>
                                <PdpSharedAuctionClosed
                                    item={item}
                                    currency={currency}
                                    isAuctionSiderailRedesignVariant={
                                        isAuctionSiderailRedesignVariant
                                    }
                                />
                            </ClientSuspense>
                        )}
                        {seller?.isAccessible && (
                            <ClientSuspense fallback={null}>
                                <div className={`${styles.sidebarSection} ${styles.printHide}`}>
                                    <PdpRespUnavailableSidebarContact user={user} item={item} />
                                    <PdpRespUnavailableSidebarSellerInfo user={user} item={item} />
                                </div>
                            </ClientSuspense>
                        )}
                        <ClientSuspense fallback={null}>
                            <div className={styles.printShow}>
                                <PdpRespSharedQrCode item={item} />
                            </div>
                        </ClientSuspense>
                    </div>
                </div>
            </div>

            <div className={styles.printHide}>
                <PdpRespUnavailableDetailsTabs user={user} item={item} viewer={viewer} />
            </div>
            <div className={styles.printShow}>
                <PdpRespSharedPrintDetailsLazy item={item} user={user} currency={currency} />
            </div>

            {fetchChat && <PdpRespSharedSellerChatLoader viewer={viewer} currency={currency} />}

            <div className={styles.printHide}>
                {PdpRespSharedItemsRefetchCarouselConfig.map(
                    ({ query, title, key, getTableMetadata, dataTn }) => {
                        const { isVisible, viewAllLinkData, trackingList, favoritesLocation } =
                            getTableMetadata({
                                seller,
                                itemId,
                            });

                        if (!isVisible) {
                            return null;
                        }

                        return (
                            <PdpRespSharedItemsRefetchCarousel
                                key={key}
                                user={user}
                                title={title}
                                dataKey={key}
                                viewer={viewer}
                                viewerQuery={query}
                                currency={currency}
                                trackingList={trackingList}
                                disableFavorites={!afterDisplay}
                                viewAllLinkData={viewAllLinkData}
                                favoritesLocation={favoritesLocation}
                                dataTn={dataTn}
                            />
                        );
                    }
                )}

                {isAuctionModuleDlosPDPVariant && (
                    <PdpRespSimilarAuctionItemsLazy
                        user={user}
                        viewer={viewer}
                        currency={currency}
                        trackingLocation="sold unavailable pdp auction you may also like"
                    />
                )}

                <RecentlyViewedItemsProvider excludedItemId={item.serviceId}>
                    <RecentlyViewedItemsLazy
                        itemsPerPage={5}
                        currency={currency}
                        authModalLoader={authModalLoader}
                        wrapperClassName={classnames(styles.printHide, styles.recentlyViewed, {
                            [styles.divider]: viewer.isWordpressArticlesEnabled,
                        })}
                    />
                </RecentlyViewedItemsProvider>
            </div>

            {isWordpressArticlesEnabled && (
                <div className={styles.printHide}>
                    <PdpSharedEditorialArticles item={item} />
                </div>
            )}

            {isEnhancedBuyerIncentivesClientVariant === false && (
                <div className={styles.printHide}>
                    <PdpRespSharedBuyerAssurance user={user} />
                </div>
            )}

            <div className={styles.printHide}>
                <SharedRespLinksBlock
                    item={item}
                    viewer={viewer}
                    itemSearch={null}
                    onClick={() => {
                        trackEvent({
                            category: 'product',
                            action: 'more ways to browse link click',
                        });
                    }}
                    links={internalBuyLinks?.filter(filterNulls) || null}
                />
            </div>

            {showOptinPrompt && (
                <ClientSuspense fallback="">
                    <PdpSharedOptInPromptModal
                        isAuctionEnabled={false}
                        onModalClose={() => setShowOptinPrompt(false)}
                    />
                </ClientSuspense>
            )}
        </>
    );
};
